import { createSelector } from "reselect";

import { selectors as step1Selectors } from "../Step1";
import { selectors as styleSelectors } from "../WizardStyleChoice";
import { selectors as layoutSelectors } from "../WizardLayoutChoice";
import { selectors as colorSelectors } from "../WizardColorChoice";
import { selectors as step5Selectors } from "../Step5";
import * as constants from "./constants";
import * as routingSelectors from "../../store/selectors";
import { ATT_MATRIX_VALS, ROUTES } from "../Wizard/constants";
import { WizardNavItemDerivedState as NavItemState } from "./components/NavItem";

export const currentRoute = createSelector(
  routingSelectors.location,
  location => {
    if (!location) {
      return constants.ROUTES.default;
    }

    if (location.pathname === "/wizard/step-1") {
      return constants.ROUTES.step1;
    }

    if (location.pathname === "/wizard/step-1/products") {
      return constants.ROUTES.step1b;
    }

    if (/^\/wizard\/step-1\/products\/\d+\/?$/.test(location.pathname)) {
      return constants.ROUTES.step1c;
    }

    if (location.pathname === "/wizard/step-2") {
      return constants.ROUTES.step2;
    }

    if (location.pathname === "/wizard/step-3") {
      return constants.ROUTES.step3;
    }

    if (location.pathname === "/wizard/step-4") {
      return constants.ROUTES.step4;
    }

    if (location.pathname === "/wizard/step-5") {
      return constants.ROUTES.step5;
    }

    return constants.ROUTES.default;
  },
);

export const nextWizardStep = createSelector(currentRoute, route => route.next);
export const prevWizardStep = createSelector(currentRoute, route => route.prev);

export const isWizardNextStepDisabled = createSelector(
  step1Selectors.chosenProduct,
  layoutSelectors.chosenLayout,
  styleSelectors.selectedStyle,
  styleSelectors.descriptionValue,
  routingSelectors.location,
  (product, layout, selectedStyle, customStyle, location) => {
    // Should always be disabled on these
    if (!location) {
      return true;
    }

    // Product must be chosen for step 1 to proceed, in any state of step 1
    if (
      ["/wizard/step-1", "/wizard/step-1/products"].includes(
        location.pathname,
      ) ||
      /^\/wizard\/step-1\/products\/\d+\/?$/.test(location.pathname)
    ) {
      return product == null;
    }

    // A layout must be chosen for step 2 to proceed (if in att matrix)
    if (location.pathname === "/wizard/step-2") {
      // If no product, then they can't proceed
      if (product == null) {
        return true;
      }

      // If required then layout must be defined
      if (product.attribute.layout === ATT_MATRIX_VALS.REQ) {
        return layout == null;
      }

      // Default case: allow user to proceed
      return false;
    }

    // Style must be chosen for step 3 to proceed (if in att matrix)
    if (location.pathname === "/wizard/step-3") {
      // If no product, then they can't proceed
      if (product == null) {
        return true;
      }

      // If required then must check at least 1 style set (or custom style)
      if (product.attribute.style === ATT_MATRIX_VALS.REQ) {
        return !(selectedStyle != null || customStyle.length > 0);
      }

      // Default case: allow user to proceed
      return false;
    }

    return true; // Everything else is disabled
  },
);

const defaultNavItemState: NavItemState = {
  active: false,
  disabled: false,
  checked: false,
};

export const step1NavItem = createSelector(
  step1Selectors.chosenProduct,
  routingSelectors.location,
  (product, location): NavItemState => {
    if (!location) {
      return defaultNavItemState;
    }

    const active = location.pathname.startsWith(ROUTES.step1.curr);
    const checked = product != null;

    return {
      active,
      checked,
      disabled: active || !checked,
    };
  },
);

export const step2NavItem = createSelector(
  step1Selectors.chosenProduct,
  layoutSelectors.chosenLayout,
  routingSelectors.location,
  (product, layout, location): NavItemState => {
    if (!location || !product) {
      return defaultNavItemState;
    }

    const active = location.pathname === ROUTES.step2.curr;
    let checked = false;

    if (product.attribute.layout !== ATT_MATRIX_VALS.NA) {
      checked = layout != null;
    }

    return {
      active,
      checked,
      disabled: active || !checked,
    };
  },
);

export const step3NavItem = createSelector(
  step1Selectors.chosenProduct,
  styleSelectors.selectedStyle,
  styleSelectors.descriptionValue,
  routingSelectors.location,
  (product, selectedStyle, customStyle, location): NavItemState => {
    if (!location || !product) {
      return defaultNavItemState;
    }

    const active = location.pathname === ROUTES.step3.curr;
    const checked =
      product.attribute.style !== ATT_MATRIX_VALS.NA &&
      (selectedStyle != null || customStyle.length > 0);

    return {
      active,
      checked,
      disabled: active || !checked,
    };
  },
);

export const step4NavItem = createSelector(
  step1Selectors.chosenProduct,
  colorSelectors.chosenColor,
  routingSelectors.location,
  (product, color, location): NavItemState => {
    if (!location || !product) {
      return defaultNavItemState;
    }

    const active = location.pathname === ROUTES.step4.curr;

    // TODO: Proper check against req'd form values
    let checked = false;
    if (product.attribute.primary_color !== ATT_MATRIX_VALS.NA) {
      checked = true;
    }

    return {
      active,
      checked,
      disabled: active || !checked,
    };
  },
);

export const step5NavItem = createSelector(
  step1Selectors.chosenProduct,
  step5Selectors.savedFormValues,
  routingSelectors.location,
  (product, formValues, location): NavItemState => {
    if (!location || !product) {
      return defaultNavItemState;
    }

    const active = location.pathname === ROUTES.step5.curr;
    // TODO: Proper check against req'd form values
    const checked =
      formValues.customer_email != null && !!formValues.customer_email.length;

    return {
      active,
      checked,
      disabled: active || !checked,
    };
  },
);



// WEBPACK FOOTER //
// ./src/routes/Wizard/selectors.ts